import { ConnectionHandles } from '@/components/diagrams/components/ConnectionHandles/ConnectionHandles';
import {
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TILE_DEFAULT_VARIANT,
} from '@/components/diagrams/components/Tile/constants';
import { TileFooterLabel } from '@/components/diagrams/components/Tile/TileFooterLabel';
import { DiagramNodeDisplay } from '@/components/diagrams/diagramStyle';
import {
  TileNodeBase,
  type TileNodeProps,
} from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';
import { DiagramNodeKind } from '@/types/schema';

import { useDiagramTextFields } from '../../hooks/useDiagramTextFields';
import { type ReactFlowDiagramNodeData } from '../../utils/reactFlowDiagram';
import { DiagramShapeNode } from './DiagramShapeNode';
import { DiagramTextNode } from './DiagramTextNode';

/**
 * A builder tile or icon. It places the text fields of the node in the
 * shared tile: its title as `lineOne`, its notes as `notes`, and its footer
 * as `footerLabel`, as in the Figma tile anatomy. The fields keep their own
 * typography in the shared tile.
 */
function DiagramTile(props: TileNodeProps) {
  const { id, data, isConnectable } = props;
  const {
    variant = TILE_DEFAULT_VARIANT,
    customColor,
    fontSizeVariant = TILE_DEFAULT_FONT_SIZE_VARIANT,
    componentType,
    slots,
  } = data as ReactFlowDiagramNodeData;
  const isIcon = componentType === DiagramNodeDisplay.TileIcon;
  const { renderField, onDoubleClick } = useDiagramTextFields(props);

  const footer = renderField('footerLabel');
  return (
    <>
      <TileNodeBase
        {...props}
        data={{
          ...data,
          lineOne: renderField('label'),
          lineTwo: renderField('subtitle'),
          notes: renderField('detail'),
          // A tile shows its footer in a band. An icon shows its footer text
          // below its title.
          slots: {
            ...slots,
            FooterLabel:
              footer &&
              (isIcon ? (
                footer
              ) : (
                <TileFooterLabel
                  tileVariant={variant}
                  tileCustomColor={customColor}
                  fontSizeVariant={fontSizeVariant}
                >
                  {footer}
                </TileFooterLabel>
              )),
          },
          onDoubleClick,
        }}
      />
      {isConnectable && <ConnectionHandles nodeId={id} />}
    </>
  );
}

/**
 * Draws a builder node by its kind and display: standalone text, a shape,
 * or the shared tile. Each node has a connection handle on each side while
 * the canvas accepts new connections. The user drags from a handle to
 * connect the node to another node.
 *
 * All builder nodes have the `tile` node type, so the layout, the alignment
 * lines, and the stages treat them the same.
 */
export function DiagramTileNode(props: TileNodeProps) {
  const { item, componentType } = props.data as ReactFlowDiagramNodeData;
  if (componentType === DiagramNodeDisplay.Shape) {
    return <DiagramShapeNode {...props} />;
  }
  switch (item.kind) {
    case DiagramNodeKind.Text:
      return <DiagramTextNode {...props} />;
    case DiagramNodeKind.Shape:
    case DiagramNodeKind.Person:
    case DiagramNodeKind.Entity:
    case DiagramNodeKind.Document:
    case DiagramNodeKind.Asset:
    case DiagramNodeKind.Summary:
    case DiagramNodeKind.Payment:
      return <DiagramTile {...props} />;
  }
}
